import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetDashboardResult } from '@whinerp/client';
import {
  ChartWidget,
  EmptyState,
  ErrorState,
  MetricTile,
  Panel,
  SectionLabel,
  Skeleton,
  type ChartSegment,
} from '@whinerp/ui';
import { absent, formatCount, formatDecimal, formatMoney, formatPercent } from '@whinerp/contracts/format';
import { sumDecimals } from '@whinerp/contracts/money';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-24 Dashboard.
 *
 * A twelve-column grid of widgets, each of which is a saved question. Three
 * things decide the shape.
 *
 * **Every widget drills through in one click.** A dashboard that cannot be
 * interrogated becomes wallpaper, and the first question anybody asks of a
 * tile is which records. Each segment carries its own link, and the heading
 * opens the full report.
 *
 * **A failed widget fails in its own tile.** The server resolves each widget
 * separately and attaches either figures or a reason; one broken tile must not
 * take eleven working ones down with it, and a blank tile is indistinguishable
 * from a slow one.
 *
 * **Widgets stack to one column on a narrow screen.** The grid is a layout,
 * not a promise about pixel positions - a twelve-column grid rendered on a
 * phone is twelve unreadable slivers.
 */

type Dashboard = GetDashboardResult;
type Widget = Dashboard['widgets'][number];

/**
 * A widget's column span, as a Tailwind class.
 *
 * A lookup rather than an interpolated class name: Tailwind scans source text,
 * so `col-span-${n}` produces a class that exists in the markup and in no
 * stylesheet - a widget that silently occupies one column instead of six.
 */
const SPANS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

function DashboardScreen(): JSX.Element {
  const params = useParams();
  const key = params['id'] ?? 'home';

  const dashboard = useQuery<Dashboard, ApiError>({
    queryKey: ['dashboard', key],
    queryFn: () => api.getDashboard({ path: { key } }),
  });

  if (dashboard.isPending) return <DashboardLoading />;
  if (dashboard.isError) {
    return <DashboardError error={dashboard.error} retry={() => void dashboard.refetch()} />;
  }
  if (dashboard.data.widgets.length === 0) return <DashboardEmpty reason="no-records" />;

  return (
    <div data-component="Dashboard" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <SectionLabel>{dashboard.data.name}</SectionLabel>
        {dashboard.data.description ? (
          <p className="text-meta text-ink-meta">{dashboard.data.description}</p>
        ) : null}
      </header>

      {/* One column until md, then twelve. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-page" data-widget-grid>
        {dashboard.data.widgets.map((widget) => (
          <div
            key={widget.widgetId}
            data-widget={widget.widgetId}
            data-widget-kind={widget.kind}
            className={SPANS[widget.colSpan] ?? SPANS[4]}
          >
            <Panel>
              <WidgetBody widget={widget} />
            </Panel>
          </div>
        ))}
      </div>
    </div>
  );
}

function WidgetBody({ widget }: { widget: Widget }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const result = widget.result;

  const heading = (
    <header className="flex items-baseline justify-between gap-2 pb-1">
      <SectionLabel>{widget.title}</SectionLabel>
      {widget.reportId ? (
        // The heading is the way to the full report. A tile answers one
        // question; the report behind it answers the follow-ups.
        <a
          href={`/reports/${widget.reportId}`}
          data-open-report={widget.widgetId}
          className="shrink-0 text-meta text-bronze-text underline underline-offset-4"
        >
          {t('dashboard.openReport')}
        </a>
      ) : null}
    </header>
  );

  if (widget.failure !== null || !result) {
    return (
      <>
        {heading}
        <p
          role="alert"
          data-widget-failed={widget.widgetId}
          className="rounded-card bg-danger-tint px-3 py-2 text-dense text-danger-text"
        >
          {widget.failure ?? t('dashboard.widgetFailed')}
        </p>
      </>
    );
  }

  if (widget.kind === 'kpi') {
    /*
     * The figure is every group added up, not the first one.
     *
     * This read `result.groups[0].values[0]` - so a KPI titled "Open pipeline"
     * over a report grouped by stage showed the value of whichever stage came
     * back first, beside a count of every record. Qualifying's 173,500 against
     * eleven deals. Both numbers were real; neither described the other, and
     * nothing on the tile said which was which (P-36).
     */
    const figure = kpiFigure(result, format, t);
    return (
      <>
        {heading}
        <MetricTile
          data-widget-metric={widget.widgetId}
          align="start"
          label={result.label}
          value={figure.value}
          /*
           * The count the figure actually covers, and when that is fewer than
           * the records behind it, both numbers.
           *
           * A money total is the workspace's own currency only. Saying "8 of 11
           * deals" is the difference between a smaller figure than expected
           * and a figure nobody can explain.
           */
          context={figure.context}
          labels={{
            up: (change) => change,
            down: (change) => change,
            flat: '',
          }}
          // One click to the records. With several groups the figure covers
          // all of them, so the link only appears when there is a single group
          // it could honestly open.
          {...(result.groups.length === 1 && result.groups[0]?.drillThrough
            ? { href: result.groups[0].drillThrough }
            : {})}
        />
        {result.escalation ? (
          <p data-widget-scope={widget.widgetId} className="pt-1 text-meta text-ink-meta">
            {t('report.widerThanRows', {
              values: { withheld: formatCount(result.escalation.withheld, format) },
            })}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <>
      {heading}
      <ChartWidget
        data-widget-chart={widget.widgetId}
        shape={widget.kind === 'funnel' ? 'funnel' : widget.kind === 'table' ? 'table' : result.chart}
        segments={segmentsOf(result, format, t)}
        scope={
          result.escalation
            ? { withheld: formatCount(result.escalation.withheld, format) }
            : undefined
        }
        labels={{
          chart: widget.title,
          drillThrough: (segment) => t('report.drillThrough', { values: { segment } }),
          empty: t('report.empty'),
          group: t('report.group'),
          value: t('report.value'),
          widerThanRows: (withheld) => t('report.widerThanRows', { values: { withheld } }),
          noDrillThrough: t('report.noDrillThrough'),
          /*
           * Signed, formatted, and naming what it is a change from.
           *
           * A funnel ordered by value rather than by sequence will have steps
           * that rise, and calling a rise a drop of minus eleven hundred per
           * cent is worse than saying nothing. The sign carries the direction
           * and the words carry the comparison.
           */
          stepChange: (ratio, previousStep) =>
            t('chart.ofPrevious', {
              values: {
                change: formatPercent(ratio, format, { sign: true }),
                step: previousStep,
              },
            }),
        }}
      />
    </>
  );
}

/**
 * A summary value, formatted by what it is.
 *
 * The dashboard knew the field type all along - `summaries[].field.type` -
 * and never used it, so every money figure on every widget rendered as a raw
 * decimal string. One formatter is a non-negotiable of this project and this
 * was the one place nothing went through it.
 */
function formatSummary(
  value: string,
  type: string | null,
  format: ReturnType<typeof useFormat>,
): string {
  if (type === 'money') return formatMoney(value, format);
  if (type === 'number') return formatDecimal(value, format);
  return value;
}

/**
 * The headline figure for a KPI widget, and the count that describes it.
 *
 * Summed across groups in the browser rather than asked for separately: the
 * groups are already here, and a second query would be a second answer to
 * "what is the total" that could disagree with the rows beneath it.
 */
function kpiFigure(
  result: NonNullable<Widget['result']>,
  format: ReturnType<typeof useFormat>,
  t: ReturnType<typeof useT>,
): { value: string; context: string } {
  const summary = result.summaries[0];
  const counted = result.groups.reduce((total, group) => total + group.countedInCurrency, 0);

  if (!summary || summary.fn === 'count') {
    return {
      value: formatCount(result.totalCount, format),
      context: t('list.status.records', { values: { count: formatCount(result.totalCount, format) } }),
    };
  }

  /*
   * Only a sum adds up across groups. An average of averages is not an
   * average, and the smallest of several minimums is a minimum - so sum is
   * computed here and the rest say which group they came from rather than
   * inventing a total nobody computed.
   */
  if (summary.fn !== 'sum') {
    const only = result.groups.length === 1 ? result.groups[0] : null;
    const value = only?.values[0];
    return {
      value:
        value === null || value === undefined
          ? absent('no-rows', format).text
          : formatSummary(value, summary.field?.type ?? null, format),
      context: only
        ? t('list.status.records', { values: { count: formatCount(only.count, format) } })
        : '',
    };
  }

  /*
   * Added as scaled integers, not as floats.
   *
   * The first version did `sum + Number(value)` and then `toFixed(2)`, which
   * is float arithmetic on money - the thing D-039 exists to prevent, and the
   * lint rule caught it before this sentence was written. `sumDecimals` adds
   * the decimal strings the server sent, exactly.
   */
  const values = result.groups.map((group) => group.values[0]).filter((value): value is string => value !== null);
  const total = sumDecimals(values);
  return {
    value: formatSummary(total, summary.field?.type ?? null, format),
    context:
      counted < result.totalCount
        ? t('report.inWorkspaceCurrency', {
            values: {
              counted: formatCount(counted, format),
              count: formatCount(result.totalCount, format),
            },
          })
        : t('list.status.records', { values: { count: formatCount(result.totalCount, format) } }),
  };
}

/** Groups as segments. The formatted figure and its magnitude travel separately. */
function segmentsOf(
  result: NonNullable<Widget['result']>,
  format: ReturnType<typeof useFormat>,
  t: ReturnType<typeof useT>,
): ChartSegment[] {
  const summary = result.summaries[0];
  const isCount = !summary || summary.fn === 'count';
  return result.groups.map((group) => {
    const first = group.values[0];
    const magnitude = isCount ? group.count : Number(first ?? 0);
    return {
      key: group.key.join('|') || 'all',
      label: group.label,
      // Through the formatter, like every other number a person reads. These
      // were raw decimal strings: "173500.50" on a dashboard, in a product
      // whose whole premise is one formatter (D-018).
      value: isCount
        ? formatCount(group.count, format)
        : first === null || first === undefined
          ? absent('no-rows', format).text
          : formatSummary(first, summary?.field?.type ?? null, format),
      magnitude: Number.isFinite(magnitude) ? magnitude : 0,
      detail: isCount
        ? undefined
        : [
            group.countedInCurrency < group.count
              ? t('report.countedOf', {
                  values: {
                    counted: formatCount(group.countedInCurrency, format),
                    count: formatCount(group.count, format),
                  },
                })
              : formatCount(group.count, format),
          ],
      ...(group.drillThrough ? { href: group.drillThrough } : {}),
    };
  });
}

function DashboardEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="SquaresFour"
      title={t('dashboard.empty.title')}
      body={t('dashboard.empty.body')}
      primary={{ label: t('empty.action.addWidget'), icon: 'Plus', href: '/revenue' }}
    />
  );
}

function DashboardLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {/* Per-widget skeletons, in the grid the widgets will land in. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-page">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="md:col-span-6">
            <Skeleton className="h-plot w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardErrorState({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This dashboard could not be loaded"
      body={error instanceof Error ? error.message : 'The dashboard did not load.'}
      onRetry={retry}
    />
  );
}

const DashboardError = DashboardErrorState;

export const screen = defineScreen({
  id: 'SC-24',
  route: '/dashboards/:id',
  surface: 'page',
  title: 'Dashboard',
  features: ['F-098'],
  entitlement: 'F-098',
  reachedBy: [{ kind: 'nav', item: 'reports' }],
  components: ['ChartWidget', 'MetricTile'],
  elements: ['Dashboard'],
  sampleParams: { id: 'home' },
  states: {
    empty: DashboardEmpty,
    loading: DashboardLoading,
    error: DashboardErrorState,
  },
  component: DashboardScreen,
});

export default screen;
